import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { ArrowRight, Search, Star, ShieldCheck, ChevronRight } from "lucide-react";

const OFFERINGS = [
  { title: "One-on-One Mentoring", desc: "Personalized guidance from industry veterans" },
  { title: "Executive Coaching", desc: "Accelerate your leadership journey" },
  { title: "Advisory Sessions", desc: "Strategic counsel for critical decisions" },
  { title: "Idea Validation", desc: "Get expert feedback on your startup or initiative" },
  { title: "Career Guidance", desc: "Navigate transitions with experienced mentors" },
  { title: "Board Advisory", desc: "Access board-level expertise on demand" },
  { title: "Speaking Engagements", desc: "Invite thought leaders to inspire your team" },
  { title: "Corporate Workshops", desc: "Tailored learning experiences for organizations" },
];

const INDUSTRIES = [
  "Technology", "Banking & Finance", "Healthcare", "Manufacturing",
  "Retail & Consumer", "Education", "Consulting", "Startups"
];

const TESTIMONIALS = [
  { name: "Rohit Gupta", role: "Founder, TechStart", text: "CouncilOne connected me with a seasoned CFO who transformed our fundraising strategy. Raised Series A in 4 months.", rating: 5 },
  { name: "Meera Pillai", role: "VP Engineering, FinCorp", text: "The quality of experts on CouncilOne is unmatched. My mentor had exactly the experience I needed to lead my team.", rating: 5 },
  { name: "Aditya Shah", role: "MBA Student, IIM-A", text: "As a student, I couldn't believe I could access C-suite leaders for career advice. Changed the direction of my career.", rating: 5 },
];

export default function LandingPage() {
  const [stats, setStats] = useState({ council_members: 0, explorers: 0, sessions_completed: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();

  // Guests must register before accessing anything — gate every destination
  const gate = (path) => (user ? path : "/login");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsRes = await fetch(`${API}/stats`);
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      } catch {
        // stats are decorative on the landing page; fall back to static values
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(user ? `/discover${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""}` : "/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />

      {/* HERO */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <p className="section-label mb-5">The Expert Network</p>
            <h1
              className="text-5xl sm:text-6xl font-normal text-[#0F172A] leading-none mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Wisdom on demand.<br />
              <em className="text-[#064E3B]">Decisions made well.</em>
            </h1>
            <p className="text-base text-[#52525B] leading-relaxed max-w-xl mb-8">
              CouncilOne brings accomplished professionals within reach. Access decades of expertise through structured, trusted engagements — whenever you need clarity.
            </p>

            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-0 max-w-lg mb-8">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="text"
                  placeholder="Search by expertise, industry, or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#E4E4E1] border-r-0 bg-white text-sm text-[#0F172A] placeholder-[#A1A1AA] focus:outline-none focus:border-[#0F172A] rounded-l-sm"
                  data-testid="hero-search-input"
                />
              </div>
              <button
                type="submit"
                className="bg-[#0F172A] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1E293B] transition-colors rounded-r-sm"
                data-testid="hero-search-btn"
              >
                Search
              </button>
            </form>

            <div className="flex flex-wrap gap-3">
              <Link to={gate("/discover")} className="btn-primary flex items-center gap-2 text-sm" data-testid="hero-find-experts">
                Find Your Expert <ArrowRight size={14} />
              </Link>
              <Link to="/login" className="btn-secondary flex items-center gap-2 text-sm" data-testid="hero-join-explorer">
                Join as Explorer
              </Link>
            </div>
          </div>

          {/* Right panel - premium visual (no member identities shown publicly) */}
          <div className="md:col-span-5 hidden md:block">
            <div className="relative">
              <div className="rounded-sm overflow-hidden border border-[#E4E4E1] shadow-sm">
                <img
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80"
                  alt="Professional advisory session"
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white border border-[#E4E4E1] shadow-md rounded-sm px-5 py-4 flex items-center gap-3">
                <div className="w-9 h-9 bg-[#064E3B]/10 rounded-full flex items-center justify-center">
                  <ShieldCheck size={18} className="text-[#064E3B]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#0F172A]">Vetted, invite-only council</p>
                  <p className="text-[10px] text-[#52525B]">Register to meet your experts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0F172A] py-12 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: "Council Members", value: stats.council_members || "500+" },
            { label: "Industries Covered", value: "15+" },
            { label: "Sessions Completed", value: stats.sessions_completed || "2,000+" },
            { label: "Explorer Satisfaction", value: "4.9★" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
              <p className="text-sm text-white/60">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BROWSE BY INDUSTRY */}
      <section className="py-16 bg-white border-y border-[#E4E4E1] px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="section-label mb-2">Explore by Domain</p>
            <h2 className="text-3xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Expertise across industries
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {INDUSTRIES.map((ind) => (
              <Link
                key={ind}
                to={gate(`/discover?industry=${encodeURIComponent(ind)}`)}
                className="flex items-center gap-2 border border-[#E4E4E1] bg-[#FAFAF8] text-sm text-[#0F172A] px-5 py-2.5 rounded-sm hover:border-[#0F172A] hover:bg-white transition-colors font-medium"
                data-testid={`industry-${ind.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {ind} <ChevronRight size={14} className="text-[#A1A1AA]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section id="offerings" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-10">
          <p className="section-label mb-2">How it Works</p>
          <h2 className="text-3xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Every format of expertise
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {OFFERINGS.map((o) => (
            <div
              key={o.title}
              className="p-5 border border-[#E4E4E1] bg-white rounded-sm hover:border-[#0F172A] transition-colors cursor-pointer"
              onClick={() => navigate(gate("/discover"))}
              data-testid={`offering-${o.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <h4 className="text-sm font-semibold text-[#0F172A] mb-2">{o.title}</h4>
              <p className="text-xs text-[#52525B] leading-relaxed">{o.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-[#0F172A] px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <p className="section-label text-white/60 mb-2">Testimonials</p>
            <h2 className="text-3xl font-normal text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              What our Explorers say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white/5 border border-white/10 p-8 rounded-sm">
                <div className="flex mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={`${t.name}-star-${i}`} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-6 italic">&ldquo;{t.text}&rdquo;</p>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-white/50 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <p className="section-label mb-4">Get started today</p>
        <h2 className="text-4xl font-normal text-[#0F172A] mb-5 max-w-2xl mx-auto" style={{ fontFamily: "'Playfair Display', serif" }}>
          Your best decision starts with the right advice
        </h2>
        <p className="text-[#52525B] text-base mb-8 max-w-lg mx-auto">
          Join thousands of professionals who&apos;ve accelerated their careers and businesses through CouncilOne.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to={gate("/discover")} className="btn-primary flex items-center gap-2" data-testid="cta-find-experts">
            Find Your Expert <ArrowRight size={16} />
          </Link>
          <Link to="/login" className="btn-secondary" data-testid="cta-register">
            Create Free Account
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0F172A] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center">
                  <span className="text-[#0F172A] text-xs font-bold font-serif">C</span>
                </div>
                <span className="text-white font-semibold text-sm">CouncilOne</span>
              </div>
              <p className="text-white/50 text-xs max-w-xs leading-relaxed">
                The world&apos;s most trusted expert network for meaningful professional connections.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-xs">
              <div>
                <p className="text-white/40 uppercase tracking-widest font-semibold mb-3">Platform</p>
                <div className="space-y-2">
                  <Link to={gate("/discover")} className="block text-white/70 hover:text-white transition-colors">Find Experts</Link>
                  <Link to={gate("/knowledge")} className="block text-white/70 hover:text-white transition-colors">Knowledge Hub</Link>
                  <Link to="/login" className="block text-white/70 hover:text-white transition-colors">Join as Explorer</Link>
                </div>
              </div>
              <div>
                <p className="text-white/40 uppercase tracking-widest font-semibold mb-3">Council</p>
                <div className="space-y-2">
                  <a href="#" className="block text-white/70 hover:text-white transition-colors">For Experts</a>
                  <a href="#" className="block text-white/70 hover:text-white transition-colors">Nomination</a>
                  <a href="#" className="block text-white/70 hover:text-white transition-colors">Guidelines</a>
                </div>
              </div>
              <div>
                <p className="text-white/40 uppercase tracking-widest font-semibold mb-3">Legal</p>
                <div className="space-y-2">
                  <a href="#" className="block text-white/70 hover:text-white transition-colors">Privacy</a>
                  <a href="#" className="block text-white/70 hover:text-white transition-colors">Terms</a>
                  <a href="mailto:support@councilone.in" className="block text-white/70 hover:text-white transition-colors">Contact</a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-white/30 text-xs">© 2025 CouncilOne. All rights reserved.</p>
            <p className="text-white/30 text-xs">Built for India&apos;s knowledge economy</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
